import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Grid } from '@material-ui/core';
import styled from 'styled-components';
import cx from 'classnames';
import useInput from 'lib/hooks/useInput';
import { useDidUpdateEffect } from 'lib/utils';
import MuiButton from 'components/common/button/MuiButton';
import T from 'components/common/text/T';
import { employee_add } from 'components/base/images';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import CustomSpan from 'components/common/text/CustomSpan';
import useFileInput from 'lib/hooks/useFileInput';
import { useShallowAppSelector } from 'store/hooks';
import { EmployeeActions, UtilActions, FileActions, AppActions } from 'store/actionCreators';
import { fixedNumbering, validInputString } from 'lib/library';
import {
  IEmployeeInfo,
  IEmployeeData,
  IProfileImageData,
  IEmployeeInputData,
} from 'lib/types/accountTypes';
import { ILoginUser } from 'lib/types/sharedTypes';
import EmployeeModalInput from 'components/account/EmployeeModalInput';
import axios from 'axios';
import ListEmpty from 'components/common/list/ListEmpty';
import CircularLoading from 'components/base/loading/CircularLoading';
interface EmployeeModalProps {
  selectedEmployeeNumber: {
    value: string | null;
    onChange: (e: any) => void;
    setValue: (value: string) => void;
  };
}

function EmployeeModalContainer({ selectedEmployeeNumber }: EmployeeModalProps) {
  const {
    user,
    fetchEmployeeListAll: { list: employeeListAll },
    fetchEmployeeListAllSuccess,
    fetchEmployeeListAllPending,
    fetchEmployeeListAllFailure,
    updateEmployeeSuccess,
    registerEmployee: {
      employeeInfo: { syncUid: registerEmployeeUid },
    },
    registerEmployeeSuccess,
    deleteEmployeeSuccess,
    fetchCountryList: { countryList },
    fetchEmployeeImageUpload,
    fetchEmployeeImageUploadSuccess,
    completeImageFileUploadSuccess,
    deleteEmployeeImageSuccess,
  }: {
    user: ILoginUser;
    fetchEmployeeListAll: {
      list: IEmployeeInfo[];
    };
    fetchEmployeeListAllSuccess: boolean;
    fetchEmployeeListAllPending: boolean;
    fetchEmployeeListAllFailure: boolean;
    updateEmployeeSuccess: boolean;
    registerEmployee: {
      employeeInfo: {
        syncUid: string;
      };
    };
    registerEmployeeSuccess: boolean;
    deleteEmployeeSuccess: boolean;
    fetchCountryList: {
      countryList: any;
    };
    fetchEmployeeImageUpload: any;
    fetchEmployeeImageUploadSuccess: boolean;
    completeImageFileUploadSuccess: boolean;
    deleteEmployeeImageSuccess: boolean;
  } = useShallowAppSelector(state => ({
    user: state.user.user || {},
    fetchEmployeeListAll: state.employee.employeeListAll.data || {},
    fetchEmployeeListAllSuccess: state.employee.employeeListAll.success,
    fetchEmployeeListAllPending: state.employee.employeeListAll.pending,
    fetchEmployeeListAllFailure: state.employee.employeeListAll.failure,
    registerEmployee: state.employee.registerEmployee.data || { employeeInfo: {} },
    registerEmployeeSuccess: state.employee.registerEmployee.success,
    updateEmployeeSuccess: state.employee.updateEmployee.success,
    deleteEmployeeSuccess: state.employee.deleteEmployee.success,
    fetchCountryList: state.util.countryList.data || {},
    fetchEmployeeImageUpload: state.file.employeeImageUploadUrl.data || {},
    fetchEmployeeImageUploadSuccess: state.file.employeeImageUploadUrl.success,
    completeImageFileUploadSuccess: state.file.completeImageFileUpload.success,
    deleteEmployeeImageSuccess: state.file.deleteEmployeeImage.success,
  }));
  const [isLoadSuccess, setIsLoadSuccess] = useState<boolean>(false);
  const [isLoadFailure, setIsLoadFailure] = useState<boolean>(false);
  const { ipcRenderer } = window;

  const employeeListRowHeight = 58;
  const companyName = user?.company;
  const employeeListRef = useRef<null | any>(null);
  const selectEmployee = useInput();
  const isModifyEmployee = useInput(false);
  const isAddEmployee = useInput(false);
  const profileImage = useFileInput({
    file: null,
    name: '',
  });
  const profileIdx = useInput(null);
  const deleteProfileImageIdx = useInput(null);
  const profileImageData: IProfileImageData = {
    profileImage,
    profileIdx,
    deleteProfileImageIdx,
  };
  // 포커싱 이동이 한번만 진행 될 수 있도록 하기 위한 flag
  const [isEmployeeScrollFocus, setIsEmployeeScrollFocus] = useState<boolean>(false);
  const [extraSyncUid, setExtraSyncUid] = useState('');
  const email = useInput('');
  const employeeIdx = useInput(null);
  const authority = useInput(1);
  const name = useInput('');
  const postPosition = useInput('');
  const assignedTask = useInput('');
  const phone = useInput('');
  const phonecode = useInput('');
  const syncUid = useInput('');

  const employeeData: IEmployeeData = {
    employeeIdx,
    companyName,
    email,
    authority,
    name,
    postPosition,
    assignedTask,
    phone,
    phonecode,
    syncUid,
  };

  const handleClickEmployeeAdd = () => {
    handleInitEmployeeInfo();
    setTimeout(() => {
      // 바로 변경 시, 시점으로 인해 변경 초기화 안됨
      isAddEmployee.setValue(true);
      employeeIdx.setValue(null);
      syncUid.setValue('');
      selectEmployee.onChange({ value: null });
    }, 100);
  };

  const handleClickEmployee = (data: IEmployeeInfo) => {
    if (data.syncUid === selectEmployee.value) {
      return;
    } else {
      selectEmployee.onChange({ value: data.syncUid });
      handleSetEmployeeInfo(data);
      isModifyEmployee.setValue(false);
      isAddEmployee.setValue(false);
    }
  };

  const handleSetEmployeeInfo = (data: any) => {
    employeeIdx.setValue(data?.employeeIdx);
    syncUid.setValue(data?.syncUid);
    profileImage.setValue(data?.profileImg);
    profileIdx.setValue(data?.profileIdx);

    email.setValue(data?.email);
    authority.setValue(data?.authority);
    name.setValue(data?.name);
    postPosition.setValue(data?.postPosition);
    assignedTask.setValue(data?.assignedTask);
    phone.setValue(data?.phone);
    phonecode.setValue(data?.phonecode);
  };

  const handleInitEmployeeInfo = () => {
    email.setValue('');
    authority.setValue(1);
    name.setValue('');
    postPosition.setValue('');
    assignedTask.setValue('');
    phone.setValue('');
    phonecode.setValue('');

    profileImage.setValue({});
    profileIdx.setValue(null);
    deleteProfileImageIdx.setValue(null);
    employeeIdx.setValue(null);
    syncUid.setValue('');
  };

  const handleSubmitEmployee = (data: IEmployeeInputData) => {
    const submitData = {
      assignedTask: validInputString(data.assignedTask),
      authority: data.authority,
      email: validInputString(data.email),
      name: validInputString(data.name),
      phone: validInputString(data.phone),
      phonecode: data.phonecode,
      postPosition: validInputString(data.postPosition),
    };

    if (isAddEmployee.value) {
      EmployeeActions.register_employee_request(submitData);
    } else {
      const submitUpdateData = {
        ...submitData,
        employeeIdx: employeeIdx.value,
      };
      EmployeeActions.update_employee_request(submitUpdateData);
    }
  };

  const handleDeleteEmployee = (employeeIndex: number) => {
    const submitData = {
      employeeIdx: employeeIndex,
    };
    EmployeeActions.delete_employee_request(submitData);
  };

  const fileUpload = useCallback(
    async (data: any, file: any, deleteIdx: number, syncUid: string) => {
      try {
        const response = await axios({
          url: data.signedUrl,
          method: 'put',
          data: file.file,
        });

        if (response.status === 200) {
          FileActions.complete_image_file_upload_request({
            fileType: 'employee',
            data: {
              syncUid,
              profileIdx: data.profileIdx,
              isSuccess: 1,
              deleteProfileIdx: !!deleteIdx ? deleteIdx : null,
            },
          });
        }
      } catch (error) {
        FileActions.complete_image_file_upload_request({
          fileType: 'employee',
          data: {
            syncUid,
            profileIdx: data.profileIdx,
            isSuccess: 0,
          },
        });
        AppActions.show_toast({ type: 'error', message: (error as { message: string })?.message });
      }
    },
    [],
  );

  const handleRefreshList = () => {
    EmployeeActions.fetch_employee_list_all_request();
    setIsLoadSuccess(false);
    setIsLoadFailure(false);
  };

  useEffect(() => {
    // employee init
    EmployeeActions.fetch_employee_list_all_request();
    UtilActions.fetch_country_list_request();

    handleInitEmployeeInfo();
    return () => {
      setIsLoadSuccess(false);
      setIsLoadFailure(false);
    };
  }, []);

  useEffect(() => {
    if (!!fetchEmployeeListAllSuccess) {
      if (!!selectedEmployeeNumber.value) {
        // 선택된 employee가 있는경우
        selectEmployee.setValue(selectedEmployeeNumber.value);
        const findEmployee = employeeListAll?.find(
          (item: any) => item.syncUid === selectedEmployeeNumber.value,
        );
        handleSetEmployeeInfo(findEmployee);
        isModifyEmployee.setValue(false);
        isAddEmployee.setValue(false);
        setIsEmployeeScrollFocus(false);
      } else {
        // adding employee 버튼을 통해 들어온 경우
        isAddEmployee.setValue(true);
        isModifyEmployee.setValue(true);
        selectEmployee.setValue('');
      }
    }
  }, [fetchEmployeeListAllSuccess, employeeListAll, selectedEmployeeNumber.value]);

  useDidUpdateEffect(() => {
    if (!fetchEmployeeListAllSuccess) return;
    setIsLoadSuccess(true);
  }, [fetchEmployeeListAllSuccess]);

  useDidUpdateEffect(() => {
    if (!fetchEmployeeListAllFailure) return;
    setIsLoadFailure(true);
  }, [fetchEmployeeListAllFailure]);

  useDidUpdateEffect(() => {
    if (completeImageFileUploadSuccess || deleteEmployeeImageSuccess) {
      setIsLoadSuccess(false);
      setIsLoadFailure(false);
      EmployeeActions.fetch_employee_list_all_request();
      selectedEmployeeNumber.setValue(extraSyncUid);
    }
  }, [completeImageFileUploadSuccess, extraSyncUid, deleteEmployeeImageSuccess]);

  useDidUpdateEffect(() => {
    // employee 등록 성공 후,
    if (registerEmployeeSuccess) {
      setIsLoadSuccess(false);
      setIsLoadFailure(false);
      EmployeeActions.fetch_employee_list_all_request();
      setExtraSyncUid(registerEmployeeUid);
      if (!!profileImage.value?.file) {
        // image file 변경한 경우
        const submitData = {
          syncUid: registerEmployeeUid,
          fileName: profileImage.value.name,
          fileSize: profileImage.value.size,
        };
        FileActions.fetch_employee_image_upload_request(submitData);
      } else {
        //등록된 사진 없는 경우
        setIsLoadSuccess(false);
        setIsLoadFailure(false);
        EmployeeActions.fetch_employee_list_all_request();
        selectedEmployeeNumber.setValue(registerEmployeeUid);
      }

      if (ipcRenderer) {
        ipcRenderer.send('setManagerList', {});
      }
    }
  }, [registerEmployeeSuccess]);

  useDidUpdateEffect(() => {
    // employee update 성공 후,
    if (updateEmployeeSuccess) {
      setExtraSyncUid(syncUid.value);
      if (!!profileImage.value?.file) {
        // image file 변경한 경우
        const submitData = {
          syncUid: syncUid.value,
          fileName: profileImage.value.name,
          fileSize: profileImage.value.size,
        };
        FileActions.fetch_employee_image_upload_request(submitData);
      } else if (!profileImage.value?.file && !!deleteProfileImageIdx.value) {
        // file delete
        const submitData = {
          syncUid: syncUid.value,
          profileIdx: profileIdx.value,
        };
        FileActions.delete_employee_image_request(submitData);
      } else {
        setIsLoadSuccess(false);
        setIsLoadFailure(false);
        EmployeeActions.fetch_employee_list_all_request();
        selectedEmployeeNumber.setValue(syncUid.value);
      }
      if (ipcRenderer) {
        ipcRenderer.send('setManagerList', {});
      }
    }
  }, [updateEmployeeSuccess, syncUid]);

  useDidUpdateEffect(() => {
    // 직원리스트 스크롤 이동
    if (!isEmployeeScrollFocus && !!selectEmployee.value) {
      const currentEmployeeRow = employeeListRef.current.querySelector('.employeeList__row.active');
      const employeeIndex: number = currentEmployeeRow?.dataset?.index;
      if (employeeIndex < 6) {
        employeeListRef.current.scrollTop = 0;
      } else {
        employeeListRef.current.scrollTop = employeeListRowHeight * (employeeIndex - 5);
      }
      setIsEmployeeScrollFocus(true);
    }
  }, [selectEmployee.value, employeeListRef.current]);

  useDidUpdateEffect(() => {
    if (fetchEmployeeImageUploadSuccess) {
      fileUpload(fetchEmployeeImageUpload, profileImage.value, profileIdx.value, extraSyncUid);
    }
  }, [fetchEmployeeImageUploadSuccess, extraSyncUid]);

  useDidUpdateEffect(() => {
    if (deleteEmployeeSuccess) {
      // 직원 등록 후, input 초기화 및 선택되었던 employee data 초기화
      handleInitEmployeeInfo();
      selectEmployee.setValue(null);
      selectedEmployeeNumber.setValue('');
      setIsLoadSuccess(false);
      setIsLoadFailure(false);
      EmployeeActions.fetch_employee_list_all_request();
      UtilActions.fetch_country_list_request();

      if (ipcRenderer) {
        ipcRenderer.send('setManagerList', {});
      }
    }
  }, [deleteEmployeeSuccess]);

  return (
    <StyledEmployeeModal data-component-name="EmployeeModal">
      <h1 className="sr-only">{companyName}</h1>
      <div className="employeeModal__container">
        <section className="employeeModal__content_wrapper list">
          <h1 className="sr-only">employee list</h1>

          <div className="employeeModal__company_title">
            <CustomSpan fontSize={22} fontWeight={700} fontColor="#00A4E3">
              {companyName}
            </CustomSpan>
            <MuiButton
              className="employeeModal__employee_add "
              disableElevation
              variant="contained"
              onClick={() => {
                handleClickEmployeeAdd();
              }}
            >
              <img src={employee_add} />
              {'+ '}
              <T>GLOBAL_ADD</T>
            </MuiButton>
          </div>
          <div className="employeeModal__employee_list_wrapper">
            <div className="employeeModal__employee_list employeeList" ref={employeeListRef}>
              {!isLoadSuccess && fetchEmployeeListAllPending && (
                <div className="infinite-scroll-loading">
                  <CircularLoading />
                </div>
              )}
              {useMemo(
                () => (
                  <>
                    {!!isLoadSuccess &&
                      (employeeListAll.length > 0 ? (
                        employeeListAll?.map((item: any, index: number) => {
                          return (
                            <div className="employeeList__row_box " key={item.employeeNum}>
                              <Grid
                                container
                                data-index={index}
                                className={cx('employeeList__row ', {
                                  active: !isAddEmployee.value
                                    ? item.syncUid === selectEmployee.value
                                      ? true
                                      : false
                                    : false,
                                })}
                                onClick={() => handleClickEmployee(item)}
                              >
                                <Grid item className="employeeList__row_item ">
                                  {fixedNumbering(item.employeeNum, 3)}
                                </Grid>
                                <Grid item className="employeeList__row_item ">
                                  {item.name}
                                </Grid>
                                <Grid item className="employeeList__row_item ">
                                  {item.postPosition}
                                </Grid>
                                <Grid item className="employeeList__row_item ">
                                  {item.phone}
                                </Grid>
                                {!isAddEmployee.value && item.syncUid === selectEmployee.value && (
                                  <div className="employeeList__row_arrow">
                                    <ChevronRightIcon style={{ fontSize: '28px' }} />
                                  </div>
                                )}
                              </Grid>
                            </div>
                          );
                        })
                      ) : (
                        <ListEmpty text="GLOBAL_EMPTY_LIST_EMPLOYEE" />
                      ))}
                  </>
                ),
                [employeeListAll, selectEmployee.value, isLoadSuccess],
              )}
              {!!isLoadFailure && (
                <ListEmpty
                  text="GLOBAL_LIST_ERROR"
                  isRefreshButton={true}
                  onRefresh={handleRefreshList}
                />
              )}
            </div>
          </div>
        </section>

        {useMemo(
          () => (
            <EmployeeModalInput
              profileImageData={profileImageData}
              employeeData={employeeData}
              isAddEmployee={isAddEmployee}
              isModifyEmployee={isModifyEmployee}
              selectEmployee={selectEmployee}
              countryList={countryList}
              onDeleteEmployee={handleDeleteEmployee}
              onSubmitEmployee={handleSubmitEmployee}
            />
          ),
          [
            employeeData,
            isModifyEmployee.value,
            isAddEmployee.value,
            selectEmployee.value,
            profileImage.value,
          ],
        )}
      </div>
    </StyledEmployeeModal>
  );
}

const StyledEmployeeModal = styled.section<{}>`
  .employeeModal__employee_btn_wrapper {
    position: relative;

    .employeeModal__employee_cancel_btn {
      position: absolute;
      top: 50%;
      right: 18px;
      border: 1px solid #ffffff;
      border-radius: 18px;
      width: 36px;
      height: 36px;
      transform: translate(0, -50%);
      display: inline-flex;
      align-items: center;
      justify-content: center;
      z-index: 10;
    }
  }
  .employeeModal__container {
    display: flex;
    justify-content: space-between;

    .employeeModal__content_wrapper {
      height: 740px;
      border-radius: 15px;
      padding: 40px 30px 38px;
      width: 560px;
      background-color: #ffffff;
    }

    .employeeModal__company_title {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 30px;
      .employeeModal__employee_add {
        width: 132px;
        height: 32px;
        border-radius: 15px;
        img {
          width: 34px;
          height: 26px;
          margin-right: 3px;
        }
      }
    }
    .employeeModal__employee_list_wrapper {
      width: 100%;
      height: 620px;
      background-color: #eff1f8;
      border-radius: 15px;
      padding: 5px 15px 10px;
      .employeeModal__employee_list {
        position: relative;
        width: 100%;
        height: 605px;
        padding-right: 15px;
        margin-right: -15px;
        overflow-y: overlay;
        .employeeList__row_box {
          height: 55px;
          border-bottom: 1px solid #b2bedd;
          font-size: 13px;
          font-weight: 500;

          .employeeList__row {
            height: 100%;
            align-items: center;
            position: relative;
            .employeeList__row_item {
              &:nth-child(1) {
                width: 75px;
                padding-left: 9px;
                color: #b5b7c1;
              }
              &:nth-child(2) {
                width: 122px;
                padding-left: 5px;
                text-overflow: ellipsis;
                overflow: hidden;
                white-space: nowrap;
              }
              &:nth-child(3) {
                width: 106px;
                padding-left: 5px;
                text-overflow: ellipsis;
                overflow: hidden;
                white-space: nowrap;
              }
              &:nth-child(4) {
                width: 146px;
                padding-left: 5px;
              }
            }

            &:hover {
              &:after {
                position: absolute;
                width: 100%;
                height: 100%;
                top: 0;
                left: 0;
                content: '';
                z-index: 1;
                background-color: #33b5e4;
                opacity: 0.1;
              }
            }

            &.active {
              background-color: #00a4e3;
              border-radius: 5px 29px 29px 5px;
              border-bottom: 1px solid #b2bedd;
              position: relative;

              .employeeList__row_item {
                color: #ffffff;
              }
              .employeeList__row_arrow {
                position: absolute;
                width: 30px;
                height: 30px;
                top: 50%;
                right: 14px;
                transform: translate(0, -50%);
                border: 1px solid #ffffff;
                border-radius: 50%;
                display: flex;
                justify-content: center;
                align-items: center;
                color: #ffffff;
              }
            }
          }
        }
      }
    }
  }
`;

export default React.memo(EmployeeModalContainer);
